import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button, Field, Input, Switch, Textarea } from "@discordforge/ui";
import { accountsAndIdentities, identityDelete, identityUpdate } from "../api";
import type { Identity } from "../types";
import { useMail } from "../MailApp";

interface IdentityForm {
    label: string;
    name: string;
    email: string;
    replyTo: string;
    bcc: string;
    signature: string;
    signatureInsertBefore: boolean;
}

const emptyForm = (email: string): IdentityForm => ({
    label: "",
    name: "",
    email,
    replyTo: "",
    bcc: "",
    signature: "",
    signatureInsertBefore: false,
});

function toForm(identity: Identity): IdentityForm {
    return {
        label: identity.label,
        name: identity.name,
        email: identity.email,
        replyTo: identity.replyTo,
        bcc: identity.bcc,
        signature: identity.signature,
        signatureInsertBefore: identity.signatureInsertBefore,
    };
}

function randomId(): string {
    return typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

type SaveState = "idle" | "saving" | "saved" | "error";

export function IdentityTab() {
    const { loginData } = useMail();
    const [identities, setIdentities] = useState<Identity[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<Identity | "new" | null>(null);
    const [form, setForm] = useState<IdentityForm | null>(null);
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            const result = await accountsAndIdentities();
            setIdentities(result.Identities);
            setError("");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load identities");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const startEdit = (identity: Identity) => {
        setEditing(identity);
        setForm(toForm(identity));
        setSaveState("idle");
    };

    const startNew = () => {
        setEditing("new");
        setForm(emptyForm(loginData.Email));
        setSaveState("idle");
    };

    const cancelEdit = () => {
        setEditing(null);
        setForm(null);
        setSaveState("idle");
    };

    const save = async () => {
        if (!form || !form.email.trim()) return;
        setSaveState("saving");
        const isNew = editing === "new";
        try {
            await identityUpdate({
                ...(isNew ? { Id: randomId() } : { Id: (editing as Identity).id }),
                Label: form.label,
                Name: form.name,
                Email: form.email.trim(),
                ReplyTo: form.replyTo.trim(),
                Bcc: form.bcc.trim(),
                Signature: form.signature,
                SignatureInsertBefore: form.signatureInsertBefore,
            });
            setSaveState("saved");
            setTimeout(() => setSaveState("idle"), 1500);
            await load();
            setEditing(null);
            setForm(null);
        } catch (err) {
            setSaveState("error");
            setError(err instanceof Error ? err.message : "Failed to save identity");
        }
    };

    const remove = async (identity: Identity) => {
        if (!identity.id) {
            // The default identity cannot be deleted.
            return;
        }
        if (!window.confirm(`Delete identity “${identity.name || identity.email}”?`)) return;
        try {
            await identityDelete(identity.id);
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete identity");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 py-8 text-sm text-zinc-500">
                <Loader2 size={14} className="animate-spin" /> Loading identities…
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {error && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs font-semibold text-red-400">
                    {error}
                </div>
            )}

            {editing ? (
                <IdentityFormView
                    form={form!}
                    isNew={editing === "new"}
                    saveState={saveState}
                    onChange={setForm}
                    onSave={save}
                    onCancel={cancelEdit}
                />
            ) : (
                <>
                    <div className="flex flex-col gap-2.5">
                        {identities.map((identity) => (
                            <div
                                key={identity.id || "default"}
                                className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4"
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="truncate text-sm font-bold text-zinc-100">
                                            {identity.name || identity.email}
                                        </span>
                                        {!identity.id && (
                                            <span className="shrink-0 rounded-md border border-orange-500/30 bg-orange-500/15 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-orange-400">
                                                Default
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-0.5 truncate text-xs text-zinc-500">
                                        {identity.name && identity.name !== identity.email ? (
                                            <>
                                                {identity.name} &lt;{identity.email}&gt;
                                            </>
                                        ) : (
                                            identity.email
                                        )}
                                        {identity.signature ? " · has signature" : " · no signature"}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-1.5">
                                    <Button variant="ghost" size="sm" onClick={() => startEdit(identity)}>
                                        <Pencil size={13} /> Edit
                                    </Button>
                                    {identity.id && (
                                        <Button variant="danger" size="sm" onClick={() => remove(identity)}>
                                            <Trash2 size={13} /> Delete
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {identities.length === 0 && (
                            <div className="rounded-2xl border border-dashed border-white/15 px-5 py-8 text-center text-sm text-zinc-500">
                                No identities yet — add one to send mail with a different name or signature.
                            </div>
                        )}
                    </div>

                    <div>
                        <Button variant="secondary" size="sm" onClick={startNew}>
                            <Plus size={14} /> New identity
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

function IdentityFormView({
    form,
    isNew,
    saveState,
    onChange,
    onSave,
    onCancel,
}: {
    form: IdentityForm;
    isNew: boolean;
    saveState: SaveState;
    onChange: (form: IdentityForm) => void;
    onSave: () => void;
    onCancel: () => void;
}) {
    const set = <K extends keyof IdentityForm>(key: K, value: IdentityForm[K]) =>
        onChange({ ...form, [key]: value });

    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="mb-5 flex items-center justify-between">
                <h2 className="text-sm font-black tracking-tight">
                    {isNew ? "New identity" : "Edit identity"}
                </h2>
                <button
                    type="button"
                    onClick={onCancel}
                    className="cursor-pointer rounded-lg p-1 text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
                    aria-label="Cancel"
                >
                    <X size={16} />
                </button>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Name" hint="Shown as the sender name.">
                    <Input
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Jane Doe"
                        autoComplete="off"
                    />
                </Field>
                <Field label="Email" required>
                    <Input
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="off"
                    />
                </Field>
                <Field label="Label" hint="Optional short name, e.g. “Work”.">
                    <Input
                        value={form.label}
                        onChange={(e) => set("label", e.target.value)}
                        placeholder="Work"
                        autoComplete="off"
                    />
                </Field>
                <Field label="Reply-To">
                    <Input
                        value={form.replyTo}
                        onChange={(e) => set("replyTo", e.target.value)}
                        type="email"
                        placeholder="replies@example.com"
                        autoComplete="off"
                    />
                </Field>
                <Field label="Bcc" hint="Always blind-copy this address.">
                    <Input
                        value={form.bcc}
                        onChange={(e) => set("bcc", e.target.value)}
                        type="email"
                        placeholder="archive@example.com"
                        autoComplete="off"
                    />
                </Field>
            </div>

            <div className="mt-5">
                <Field
                    label="Signature (HTML)"
                    hint="Inserted at the end of new messages. Plain text works too."
                >
                    <Textarea
                        value={form.signature}
                        onChange={(e) => set("signature", e.target.value)}
                        rows={5}
                        placeholder="--&#10;Jane Doe — DiscordForge"
                        className="font-mono text-xs"
                    />
                </Field>
            </div>

            <div className="mt-4 flex items-center gap-3">
                <Switch
                    on={form.signatureInsertBefore}
                    onToggle={() => set("signatureInsertBefore", !form.signatureInsertBefore)}
                    aria-label="Insert signature before quoted text"
                />
                <span className="text-xs text-zinc-400">
                    Insert signature <span className="font-bold text-zinc-300">before</span> quoted text
                </span>
            </div>

            <div className="mt-6 flex items-center gap-3">
                <Button onClick={onSave} loading={saveState === "saving"}>
                    {saveState === "saved" ? (
                        <>
                            <Check size={14} /> Saved
                        </>
                    ) : (
                        "Save identity"
                    )}
                </Button>
                <Button variant="ghost" onClick={onCancel}>
                    Cancel
                </Button>
                {saveState === "error" && (
                    <span className="text-xs font-semibold text-red-400">Save failed — try again</span>
                )}
            </div>
        </div>
    );
}

